import {
    AutocompleteRenderGroupParams,
    styled,
    Typography,
} from '@mui/material';
import type { FC, HTMLAttributes, LiHTMLAttributes, ReactNode } from 'react';
import { useCallback } from 'react';

import Colors from '@renderer-ui-library/mui/base/colors.module.scss';
import FontSizes from '@renderer-ui-library/mui/base/fontSizes.module.scss';
import classNames from 'classnames';
import { SelectFilterOption } from './SelectFilterOption';
import styles from './selectItems.module.scss';

type Props = LiHTMLAttributes<HTMLLIElement> & {
    label: string;
    wrapText?: boolean;
    imageUrl?: string | null;
    description?: ReactNode;
};
export const SelectItem: FC<Props> = ({
    label,
    wrapText,
    className,
    imageUrl,
    description,
    ...props
}) => {
    return (
        <li
            {...props}
            className={classNames(className, styles.item, {
                [styles.unwrapped]: !wrapText,
            })}
            title={label}
        >
            <div className={styles.contentContainer}>
                {imageUrl && (
                    <img
                        className={styles.image}
                        alt=""
                        src={imageUrl}
                        width={36}
                        height={36}
                    />
                )}
                <div>
                    <div>{label}</div>
                    {description && (
                        <Typography
                            variant="caption"
                            color="textSecondary"
                            component="div"
                        >
                            {description}
                        </Typography>
                    )}
                </div>
            </div>
        </li>
    );
};

export const useSelectItem = (options?: { wrapText?: boolean }) => {
    const renderSelectItem = useCallback(
        (
            props: HTMLAttributes<HTMLLIElement> & { key?: string },
            option: SelectFilterOption,
            state: { selected: boolean },
        ) => (
            <SelectItem
                {...props}
                key={option.key || props.key || option.value}
                label={option.listItemLabel || option.label}
                wrapText={options?.wrapText}
                imageUrl={option.imageUrl}
                description={option.description}
            />
        ),
        [options?.wrapText],
    );

    return { renderSelectItem };
};

const GroupHeader = styled('div')(({ theme }) => ({
    position: 'sticky',
    top: '-8px',
    padding: '4px 10px',
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    borderBottom: `1px solid ${theme.palette.divider}`,
    color: Colors.primary,
    fontWeight: FontSizes.h6FontWeight,
    fontSize: FontSizes.h6FontSize,
    lineHeight: FontSizes.h6LineHeight,
    zIndex: 1,
}));

const GroupItems = styled('ul')({
    padding: 0,
});

const Group = styled('li')({
    ':not(:first-child)': {
        marginTop: '20px',
    },
});

export const renderGroup = (params: AutocompleteRenderGroupParams) => (
    <Group key={params.key}>
        <GroupHeader>{params.group}</GroupHeader>
        <GroupItems>{params.children}</GroupItems>
    </Group>
);

export const groupBy = <T extends { group?: string }>(value: T) =>
    value.group ?? '';
